'use client';
import articleIcon from '@/public/images/article_icon.svg';
import {supportBasePath} from '@/utils/constants';
import {GTMClickEventTrack} from '@/utils/gtm';
import {formatTitleForURL} from '@/utils/utils';
import {
  Button,
  FormGroup,
  Menu,
  MenuItem,
  MenuSurfaceAnchor,
  SelectDropDown,
  SelectItemDropDown,
} from '@pixel-party';
import Image from 'next/image';
import Link from 'next/link';
import {useRouter} from 'next/navigation';
import React, {useState} from 'react';
import englishData from '../../i18n/locales/en/common.json';
import spanishData from '../../i18n/locales/es/common.json';
import type {
  FeaturedArticle,
  FeaturedArticlesGridObject,
  FeaturedArticlesGridObjectValue,
} from '../FeaturedArticles/FeaturedArticlesGrid';
import styles from './SubNavigation.module.scss';

export default function SubNavigation({
  locale,
  selectedItemTitle,
  isFeaturedArticlePage,
  handleSubNavigationItemClick,
  sampleResponse,
}: {
  locale: string;
  selectedItemTitle: string;
  isFeaturedArticlePage: boolean;
  handleSubNavigationItemClick: (key: string) => void;
  sampleResponse: FeaturedArticlesGridObject;
}) {
  const slugEnable = process.env.NEXT_PUBLIC_FEATURE_FLAG_SLUG_ENABLE;
  const [title, setTitle] = React.useState(selectedItemTitle);
  const router = useRouter();

  const [openMenuIndex, setOpenMenuIndex] = useState<number>(-1);

  const handleOpen = (index: number) => {
    setOpenMenuIndex(index);
  };

  const handleClose = () => {
    setOpenMenuIndex(-1);
  };

  const handleChange = (newVal: string) => {
    const title = newVal;
    setTitle(title);
    handleSubNavigationItemClick(title);
    if (!isFeaturedArticlePage) {
      router.replace(
        `${supportBasePath}/${locale}/featured-articles?hero_banner_title=${encodeURIComponent(title)}`,
      );
      router.refresh();
    }
  };

  function getMenuItems(key: string) {
    const featuredArticlesGridObjectValue: FeaturedArticlesGridObjectValue =
      sampleResponse[key as keyof typeof sampleResponse];
    const menuItems: React.JSX.Element[] = [];
    const parentTitle = key;
    let index = 0;
    Object.keys(featuredArticlesGridObjectValue).map((key: string) => {
      const articles: FeaturedArticle[] =
        featuredArticlesGridObjectValue[
          key as keyof typeof featuredArticlesGridObjectValue
        ];

      articles?.map((article: FeaturedArticle) => {
        const slugTitle = formatTitleForURL(article?.title || '');
        index += 1;
        return menuItems.push(
          <MenuItem
            key={index}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              GTMClickEventTrack(
                'sub navigation',
                `${parentTitle.toLowerCase()} item click`,
                article?.title ?? '',
              );
              router.push(
                `${supportBasePath}/${locale}/answer/${encodeURIComponent(article.id)}/${slugEnable === 'true' ? slugTitle : ''}`,
              );
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                GTMClickEventTrack(
                  'sub navigation',
                  `${parentTitle.toLowerCase()} item click`,
                  article?.title ?? '',
                );
                router.push(
                  `${supportBasePath}/${locale}/answer/${encodeURIComponent(article.id)}/${slugEnable === 'true' ? slugTitle : ''}`,
                );
              } else if (e.key === 'Escape') {
                handleClose();
              }
            }}
          >
            <Image
              src={articleIcon}
              alt={article?.title ?? 'article'}
              width={24}
              height={24}
            />
            <Link
              className={styles.menuItemText}
              href={`${supportBasePath}/${locale}/answer/${encodeURIComponent(article.id)}/${slugEnable === 'true' ? slugTitle : ''}`}
            >
              {article?.title}
            </Link>
          </MenuItem>,
        );
      });
    });
    return menuItems.slice(0, 8);
  }

  return (
    <>
      <div className={styles.navigationContainer}>
        {Object.keys(sampleResponse).map((key: string, index) => {
          if (index > 5) {
            return;
          }
          if (isFeaturedArticlePage && index === 0 && title === '') {
            setTitle(key);
          }
          return (
            <MenuSurfaceAnchor
              key={key}
              onMouseEnter={() => handleOpen(index)}
              onMouseLeave={handleClose}
            >
              <Button
                data-testid="subnavigation-menu__item"
                className={
                  isFeaturedArticlePage && title === key
                    ? styles.navigationDataActiveItem
                    : styles.navigationData
                }
                onClick={() => {
                  const title = key;
                  setTitle(title);
                  GTMClickEventTrack(
                    'sub navigation',
                    `${key.toLowerCase()} click`,
                    title,
                  );
                  handleSubNavigationItemClick(title);
                  if (!isFeaturedArticlePage) {
                    router.replace(
                      `${supportBasePath}/${locale}/featured-articles?hero_banner_title=${title}`,
                    );
                    router.refresh();
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    handleOpen(index);
                  }
                }}
                widget={{title: key}}
              />
              {(!isFeaturedArticlePage || title !== key) &&
                openMenuIndex === index && (
                  <Menu
                    className={styles.articleList}
                    open={openMenuIndex === index}
                    anchorCorner="bottomLeft"
                    renderToPortal={false}
                  >
                    {getMenuItems(key)}
                  </Menu>
                )}
            </MenuSurfaceAnchor>
          );
        })}
      </div>

      <div className={styles.subNavigation}>
        <FormGroup className={styles.subNavigationContainer}>
          <SelectDropDown
            data-testid="subNavigation-items__select"
            value={title}
            cntstyleprop={styles.dropdown}
            onChange={handleChange}
            placeholder={
              locale === 'en'
                ? englishData?.selectTopic
                : spanishData?.selectTopic
            }
            className={styles.select}
          >
            <SelectItemDropDown
              disabled
              value={
                locale === 'en'
                  ? englishData?.selectTopic
                  : spanishData?.selectTopic
              }
              className={styles.subNavigationTitle}
            >
              <em>
                {locale === 'en'
                  ? englishData?.selectTopic
                  : spanishData?.selectTopic}
              </em>
            </SelectItemDropDown>
            {Object.keys(sampleResponse).map((key: string, index) => {
              if (index > 5) {
                return;
              }
              return (
                <SelectItemDropDown
                  key={key}
                  className={styles.subNavigationTitle}
                  indstyleprop={styles.subNavigationTitleIcon}
                  value={key}
                  onClick={() => handleSubNavigationItemClick(key)}
                >
                  {key}
                </SelectItemDropDown>
              );
            })}
          </SelectDropDown>
        </FormGroup>
      </div>
    </>
  );
}
